// Centralized Help content. Each article has a stable `slug` used as URL hash.
// Body uses simple Markdown-like blocks: lines starting with "## " are H2,
// "- " are bullet items, "> " is a callout, blank lines separate paragraphs.
// Keep each article focused: TL;DR + Why + How + Tips + Limits + Related.

export type HelpLang = 'ru' | 'en';

export interface HelpArticle {
  slug: string;
  title: string;
  tldr: string;
  body: string;
  related?: string[]; // related slugs
}

export interface HelpSection {
  id: string;
  title: string;
  emoji: string;
  articles: HelpArticle[];
}

/**
 * Russian content (primary). EN is a structural mirror with concise summaries.
 * Articles intentionally short and scannable: scaffold first, deepen later.
 */
export const HELP_CONTENT_RU: HelpSection[] = [
  {
    id: 'start',
    title: 'Начало работы',
    emoji: '🚀',
    articles: [
      {
        slug: 'about',
        title: 'Что такое ТопФокус и кому он нужен',
        tldr: 'ТопФокус — это PWA для управления продуктивностью по методологии 3-3-3-1: цели, задачи, привычки, финансы и баланс жизни в одном месте.',
        body: `## Зачем приложение
ТопФокус помогает сфокусироваться на главном: одна Главная задача дня, не более 3 целей в работе, 3 ключевых задачи в неделю.

## Кому подойдёт
- Предпринимателям и руководителям
- Фрилансерам и специалистам
- Студентам и тем, кто учится непрерывно
- Командам до 50 человек (тариф Команда)

## Что вы получите
- Единый дашборд с кольцами активности и индексом жизни
- Контроль 8 сфер жизни
- Гибкие инструменты: задачи, привычки, финансы, заметки, помодоро`,
        related: ['method-3331', 'first-7-days']
      },
      {
        slug: 'method-3331',
        title: 'Методология 3-3-3-1',
        tldr: '3 активные цели, 3 ключевые задачи в неделю, 3 повседневные практики и 1 Главная задача каждый день.',
        body: `## Принцип фокуса
Чем меньше одновременных направлений — тем выше шанс довести до конца. Метод ограничивает количество одновременной работы, чтобы вы не распылялись.

## Как применять
- Заведите не более **3 целей** в статусе active
- Каждое воскресенье выбирайте **3 ключевые задачи** на неделю
- Поддерживайте **3 ежедневные привычки** (утро/день/вечер)
- Каждое утро отмечайте **1 Главную задачу** дня — она будет подсвечена неоном

> Главная задача дня закрепляется в верхней части Плана дня и Итогов дня.`,
        related: ['main-task', 'day-plan']
      },
      {
        slug: 'signup',
        title: 'Регистрация, вход и гостевой режим',
        tldr: 'Регистрация по email и паролю. Гостевой режим работает локально без облачной синхронизации.',
        body: `## Способы входа
- **Email + пароль** — единственный способ для пользователей из РФ (требование ФЗ-152)
- **Гость** — данные хранятся только на устройстве, без бэкапа

## Восстановление пароля
Откройте экран входа → «Забыли пароль» → получите письмо со ссылкой.

## Перенос с гостевого режима
После регистрации локальные данные автоматически загружаются в облако при первом подключении.`,
        related: ['geo-restriction', 'sync']
      },
      {
        slug: 'onboarding',
        title: 'Онбординг и повторный обзор',
        tldr: '15 слайдов о ключевых возможностях. Доступен повторно в шапке (иконка плёнки).',
        body: `## Когда показывается
При первом входе. Флаг \`topfocus_onboarding_seen\` хранится в localStorage.

## Как пройти ещё раз
Нажмите иконку плёнки в верхней панели — обзор откроется снова.

## Сброс
Профиль → Настройки → «Показать обзор заново».`,
        related: ['legal-wall']
      },
      {
        slug: 'legal-wall',
        title: 'Юридическая активация (7 согласий)',
        tldr: 'После онбординга появится модальное окно с 7 обязательными документами. Без согласия приложение не запустится.',
        body: `## Что нужно подтвердить
- Политика конфиденциальности
- Пользовательское соглашение
- Согласие на обработку персональных данных
- Согласие на рассылки
- Оферта на платные тарифы
- Согласие cookie
- Правила сообщества

## После согласия
Все документы доступны в Профиле → Правовое.`,
        related: ['legal-org']
      },
      {
        slug: 'install-pwa',
        title: 'Установка PWA на устройство',
        tldr: 'Приложение можно установить как обычное на iOS, Android и десктоп.',
        body: `## iOS (Safari)
Поделиться → «На экран Домой».

## Android (Chrome)
Меню (⋮) → «Установить приложение».

## Desktop
Иконка установки в адресной строке Chrome/Edge.

## Telegram WebView
Откройте https://top-focus.ru в системном браузере, не в WebView Telegram — иначе кэш может мешать.`,
        related: ['troubleshoot-pwa']
      },
      {
        slug: 'first-7-days',
        title: 'Первые 7 дней: рекомендованный сценарий',
        tldr: 'Сценарий для быстрого вовлечения: цель → SMART-разбор → план → ежедневная рефлексия.',
        body: `## День 1–2
Создайте первую цель через SMART-мастер (+50 XP). Привяжите к Сфере жизни.

## День 3–4
Заведите 3 ежедневные привычки и 3 задачи на неделю. Отметьте Главную задачу дня.

## День 5–6
Включите Помодоро на самой важной задаче. Привязывайте таймер к задаче.

## День 7
Пройдите ежедневную рефлексию (после 18:00) и посмотрите Итоги дня.`,
        related: ['smart-wizard', 'reflection-daily']
      }
    ]
  },
  {
    id: 'navigation',
    title: 'Дашборд и навигация',
    emoji: '🧭',
    articles: [
      {
        slug: 'bento',
        title: 'Bento-сетка дашборда',
        tldr: 'Дашборд построен на адаптивной 4-колоночной Bento-сетке. Виджеты можно сворачивать.',
        body: `## Состав виджетов
- Приветствие и Фокус дня (горизонтальный аккордеон)
- Кольца активности и индекс жизни
- План дня с Главной задачей сверху
- Прогресс по сферам и целям
- Помодоро и трекер времени (свернуты по умолчанию)`,
        related: ['accordion-welcome', 'main-banner']
      },
      {
        slug: 'accordion-welcome',
        title: 'Аккордеон «Приветствие» и «Фокус дня»',
        tldr: 'Два модуля высотой 110px со свайпом между ними.',
        body: `## Управление
Свайп влево/вправо или клик по индикатору. Модули скрываются по запросу из настроек дашборда.`,
        related: ['bento']
      },
      {
        slug: 'main-banner',
        title: 'Баннер Главной задачи дня',
        tldr: 'Оранжево-красный баннер подсвечивает 1 Главную задачу. Отображается до её выполнения.',
        body: `## Как назначить
Откройте задачу → переключатель «Главная задача дня». В день может быть только одна.

## Визуальное выделение
Карточка получает неоновое свечение и закрепляется наверху Плана дня и Итогов дня.`,
        related: ['method-3331', 'day-plan']
      },
      {
        slug: 'collapsible-widgets',
        title: 'Сворачиваемые виджеты',
        tldr: 'Помодоро и Трекер времени свернуты по умолчанию для скорости загрузки и фокуса на задачах.',
        body: `## Развернуть
Кликните по заголовку виджета. Состояние сохраняется в локальном хранилище.`,
        related: ['pomodoro', 'timer']
      },
      {
        slug: 'rings',
        title: 'Кольца активности и индекс жизни',
        tldr: 'Три кольца: задачи, привычки, фокус-минуты. Индекс жизни — агрегат по 8 сферам.',
        body: `## Что показывают кольца
- Задачи: процент выполненных от запланированных на день
- Привычки: процент выполненных привычек
- Фокус: % минут под контролем (Pomodoro/Timer) от целевых

## Индекс жизни
Среднее по 8 сферам с учётом активности и баланса (Balance Spread).`,
        related: ['life-index', 'spheres']
      },
      {
        slug: 'bottom-nav',
        title: 'Нижняя навигация и кнопка «+»',
        tldr: 'Меню «+» вызывает быстрые действия: SMART-цель, задача, привычка, транзакция, пост-достижение.',
        body: `## Контекст
Действие зависит от текущей вкладки (например, на /tasks — создание задачи). На дашборде доступны все варианты.`,
        related: ['quick-actions', 'achievements-feed']
      },
      {
        slug: 'pin-lock',
        title: 'PIN/FaceID локальная блокировка',
        tldr: '4-значный PIN или биометрия. Активируется через 24 ч неактивности.',
        body: `## Включение
Профиль → Настройки → Безопасность → «Включить PIN-код».

## Сброс
Только через выход и повторный вход. Локальные данные сохраняются.`,
        related: ['security-sessions']
      },
      {
        slug: 'dev-mode',
        title: 'Dev Mode (только для суперадмина)',
        tldr: 'Позволяет переключать роли и тарифы для проверки UI без оплат.',
        body: `## Доступ
Появляется в Профиле только у пользователей с ролью \`superadmin\`.`,
        related: []
      }
    ]
  },
  {
    id: 'goals',
    title: 'Цели',
    emoji: '🎯',
    articles: [
      {
        slug: 'smart-wizard',
        title: 'SMART-мастер целей',
        tldr: '7 шагов с обратным планированием. За завершение — +50 XP.',
        body: `## Шаги
1. Сформулировать цель
2. Сделать измеримой (метрика)
3. Привязать к Сфере жизни
4. Указать дедлайн
5. Декомпозировать на задачи (обратное планирование)
6. Назначить ресурсы (контакты, бюджет)
7. Подтвердить и запустить

## Награда
+50 XP при завершении мастера.`,
        related: ['goal-card', 'spheres']
      },
      {
        slug: 'goal-sphere',
        title: 'Привязка цели к Сфере',
        tldr: 'Каждая цель должна принадлежать одной из 8 сфер. Это влияет на индекс баланса.',
        body: `## Зачем
Без сферы цель не влияет на индекс жизни и не учитывается в баланс-цветке.`,
        related: ['spheres', 'life-index']
      },
      {
        slug: 'goal-card',
        title: 'Карточка цели: задачи, привычки, контакты',
        tldr: 'В одной карточке: задачи, привычки, контакты, бюджет и затраченное время.',
        body: `## Вкладки
- Прогресс — задачи и привычки
- Аналитика — графики выполнения и расходов
- Контакты — люди, связанные с целью
- Финансы — связанные транзакции`,
        related: ['contextual-create']
      },
      {
        slug: 'goal-progress',
        title: 'Прогресс цели и аналитика',
        tldr: 'Процент рассчитывается по доле выполненных задач и накопленного времени.',
        body: `## Формула
\`progress_percent = (выполненные задачи / всего задач) * вес задач + (накопленные минуты / time_goal_minutes) * вес времени\`.`,
        related: ['goal-card']
      },
      {
        slug: 'goal-archive',
        title: 'Архивирование и завершение целей',
        tldr: 'Завершённые цели отправляются в архив с датой completed_at.',
        body: `## Восстановление
Откройте Архив → «Восстановить» — цель вернётся в активные.`,
        related: ['goal-card']
      }
    ]
  },
  {
    id: 'tasks',
    title: 'Задачи',
    emoji: '✅',
    articles: [
      {
        slug: 'task-create',
        title: 'Создание задачи',
        tldr: 'Задачи делятся на 4 типа: интеллект, рутина, коммуникация, движение.',
        body: `## Поля
- Название и иконка
- Тип (для статистики)
- Приоритет: low/medium/high
- Дедлайн
- Привязка к Цели и Сфере (обязательно)
- Длительность в минутах

> Привязка к Цели и Сфере обязательна — без неё задача не учитывается в аналитике.`,
        related: ['main-task', 'task-recur']
      },
      {
        slug: 'main-task',
        title: 'Главная задача дня (Main Task)',
        tldr: 'Одна задача в день получает статус Главной — неоновое свечение и закрепление наверху.',
        body: `## Как назначить
Открыть задачу → «Сделать главной». Назначение второй сбросит первую.`,
        related: ['method-3331', 'main-banner']
      },
      {
        slug: 'subtasks',
        title: 'Подзадачи, вложения, заметки',
        tldr: 'Декомпозируйте задачу до атомарных шагов. Добавляйте файлы и ссылки.',
        body: `## Что добавить
- Подзадачи (чек-лист)
- Заметки (текст)
- Вложения (ссылки/файлы)
- Напоминания`,
        related: ['task-create']
      },
      {
        slug: 'task-recur',
        title: 'Повторяющиеся задачи',
        tldr: 'none / daily / weekly / monthly. Базовая запись хранит правило, инстансы рисуются в календаре.',
        body: `## Логика отображения
- В списках: показывается только базовая запись
- В календарях: показываются все инстансы по правилу`,
        related: ['calendar-tasks']
      },
      {
        slug: 'task-meta',
        title: 'Приоритеты, статусы, категории, теги',
        tldr: 'Статусы: not_started / in_progress / done. Теги и категории помогают фильтровать.',
        body: `## Статусы
- not_started — не начато
- in_progress — в работе
- done — выполнено`,
        related: ['task-create']
      },
      {
        slug: 'day-plan',
        title: 'План дня и Итоги дня',
        tldr: 'Два экрана: утренний План и вечерние Итоги. PRO-пользователи получают PDF-экспорт.',
        body: `## План дня
Главная задача сверху, далее задачи по приоритету. Доступ из дашборда.

## Итоги дня
Что сделано, что перенесено, рекомендации. PDF-экспорт доступен на тарифах Profi/Premium/Команда.`,
        related: ['main-banner', 'pro-tariffs']
      },
      {
        slug: 'date-constraints',
        title: 'Ограничения изменений по датам',
        tldr: 'Нельзя изменять и отмечать задачи на будущие даты или старше 2 дней назад.',
        body: `## Зачем
Чтобы статистика отражала реальное поведение, а не задним числом подгонялась.

## Что доступно
- Сегодня и вчера: полная свобода
- Позавчера: только просмотр
- Будущее: только просмотр и редактирование плана`,
        related: []
      },
      {
        slug: 'postpone',
        title: 'Откладывание задач (Postpone)',
        tldr: 'Задачу можно перенести с указанием причины. Счётчик переносов сохраняется.',
        body: `## Лимиты
После 3 переносов задача предлагается к удалению или декомпозиции.`,
        related: ['subtasks']
      }
    ]
  },
  {
    id: 'habits',
    title: 'Привычки',
    emoji: '🔁',
    articles: [
      {
        slug: 'habit-create',
        title: 'Создание привычки',
        tldr: 'Привычки требуют привязки к Цели и Сфере. Можно выбрать дни недели.',
        body: `## Поля
- Название и иконка
- Цвет
- Дни недели (или ежедневно)
- Время напоминания`,
        related: ['habit-streak', 'habit-calendar']
      },
      {
        slug: 'habit-streak',
        title: 'Целевые дни и серии (streak)',
        tldr: 'Серия растёт при выполнении в целевой день. Пропуск целевого дня обнуляет серию.',
        body: `## Защита серии
Можно купить «заморозку» в магазине наград — серия не прервётся за один пропуск.`,
        related: ['streak-freeze']
      },
      {
        slug: 'habit-calendar',
        title: 'Календарь и тепловая карта',
        tldr: 'Месячный календарь привычки с цветовой интенсивностью по выполнению.',
        body: `## Просмотр
Откройте привычку → вкладка «Календарь». По умолчанию привычки в календарях сворачиваются.`,
        related: ['calendar-habits']
      },
      {
        slug: 'habit-tags',
        title: 'Категории и теги привычек',
        tldr: 'Группируйте привычки тегами для фильтрации и аналитики.',
        body: `## Где использовать
Фильтр привычек на главной странице, статистика по тегам.`,
        related: ['stats-tags']
      },
      {
        slug: 'habit-notif',
        title: 'Уведомления и напоминания привычек',
        tldr: 'Push в указанное время. Работает через Service Worker.',
        body: `## Лимиты
Чтобы не было спама, повторное напоминание не отправляется в течение часа.`,
        related: ['push-notifs']
      }
    ]
  },
  {
    id: 'finance',
    title: 'Финансы',
    emoji: '💰',
    articles: [
      {
        slug: 'fin-create',
        title: 'Учёт доходов и расходов',
        tldr: 'Транзакции с категорией, тегами и привязкой к Цели/Сфере.',
        body: `## Что важно
- Привязка к Цели и Сфере — обязательна
- Категория — из статичного списка
- Повторяемость: none/daily/weekly/monthly`,
        related: ['fin-link', 'fin-recur']
      },
      {
        slug: 'fin-link',
        title: 'Привязка операции к Цели и Сфере',
        tldr: 'Связывает финансовые потоки с приоритетами жизни.',
        body: `## Зачем
Видеть, во что реально вкладываются деньги — в карьеру, здоровье, семью.`,
        related: ['goal-sphere']
      },
      {
        slug: 'fin-recur',
        title: 'Категории, теги, повторяемость',
        tldr: 'Подписки и регулярные расходы создаются один раз, отображаются в календаре.',
        body: `## Логика
Базовая запись в списке, инстансы — в календаре финансов.`,
        related: ['fin-calendar']
      },
      {
        slug: 'fin-calendar',
        title: 'Календарь финансов',
        tldr: 'Месячный календарь в стиле архива со сворачиваемыми днями.',
        body: `## Цвета
Серо-зелёная палитра для финансовой ленты (\`bg-finance/15\`, \`border-finance/30\`).`,
        related: []
      },
      {
        slug: 'fin-balance-bonus',
        title: 'Бонус +5 FP за стабильный баланс',
        tldr: 'Если в течение 24 часов Balance Spread < 10 — вы получаете +5 FP уведомлением.',
        body: `## Условие
Разница между лучшей и худшей сферой меньше 10 пунктов в течение суток.`,
        related: ['life-index', 'spheres']
      }
    ]
  },
  {
    id: 'spheres',
    title: 'Сферы жизни и баланс',
    emoji: '🌸',
    articles: [
      {
        slug: 'spheres',
        title: '8 сфер жизни',
        tldr: 'Здоровье, Семья, Карьера, Финансы, Развитие, Окружение, Отдых, Смысл.',
        body: `## Что входит
К каждой сфере привязываются задачи, привычки, цели и финансы — всё считается в общий индекс.`,
        related: ['life-index']
      },
      {
        slug: 'life-index',
        title: 'Формула Индекса Жизни',
        tldr: 'Среднее по 8 сферам с учётом активности и баланса между ними.',
        body: `## Компоненты
- Активность сферы (выполненные задачи/привычки)
- Завершённость целей
- Баланс (отрицательный штраф за перекосы)`,
        related: ['flower']
      },
      {
        slug: 'flower',
        title: 'Цветок баланса и статус',
        tldr: 'Лепестки цветка отображают сферы. Цветовой статус показывает гармонию.',
        body: `## Статусы
- Гармония — все сферы в зелёной зоне
- Перекос — одна или две сферы заметно ниже
- Кризис — три и более сфер в красной зоне`,
        related: ['life-index']
      },
      {
        slug: 'life-history',
        title: 'История индекса и mindfulness',
        tldr: 'График индекса по неделям и метрика осознанности.',
        body: `## Mindfulness
Считает долю дней с пройденной рефлексией и Главной задачей.`,
        related: ['reflection-daily']
      }
    ]
  },
  {
    id: 'services',
    title: 'Сервисы',
    emoji: '🧰',
    articles: [
      {
        slug: 'pomodoro',
        title: 'Помодоро-таймер',
        tldr: 'Настраиваемые рабочие/отдых интервалы, авто-старт, длинный перерыв каждые 4 цикла.',
        body: `## Настройки
- Работа: по умолчанию 25 мин
- Короткий перерыв: 5 мин
- Длинный перерыв: 15 мин
- Сессий до длинного: 4`,
        related: ['timer']
      },
      {
        slug: 'timer',
        title: 'Трекер времени',
        tldr: 'Привязывайте таймер к задаче, цели и сфере. В полночь активный таймер останавливается.',
        body: `## Правила
- Незакреплённый таймер должен быть привязан перед сохранением
- Полночь — мягкий стоп с автосохранением`,
        related: ['pomodoro']
      },
      {
        slug: 'notes',
        title: 'Заметки, чек-листы, счётчики',
        tldr: 'Текстовые заметки можно прикреплять к задачам, чек-листы — для рутин.',
        body: `## Связь
Заметка может быть привязана к задаче и попадает в её карточку.`,
        related: ['subtasks']
      },
      {
        slug: 'misc-services',
        title: 'Курсы валют, мировое время, конвертер, калькулятор дат',
        tldr: 'Набор быстрых сервисов под рукой — без выхода из приложения.',
        body: `## Курсы валют
USD, EUR, GBP, CNY с обновлением раз в час.`,
        related: []
      },
      {
        slug: 'random-decision',
        title: 'Случайное решение',
        tldr: 'Колесо/жребий для быстрого выбора между вариантами.',
        body: `## Использование
Введите 2–10 вариантов и нажмите «Крутить».`,
        related: []
      }
    ]
  },
  {
    id: 'reflection',
    title: 'Рефлексия и Дневник успеха',
    emoji: '🪞',
    articles: [
      {
        slug: 'reflection-daily',
        title: 'Ежедневная рефлексия',
        tldr: 'Доступна с 18:00. Без пройденной рефлексии дашборд показывает напоминание.',
        body: `## Что заполняется
- Сон и стресс (1–5)
- Победа дня
- Блокеры
- Главная задача на завтра`,
        related: ['main-task', 'success-diary']
      },
      {
        slug: 'reflection-week',
        title: 'Еженедельная, месячная, годовая рефлексии',
        tldr: 'Воскресенье — неделя, последний день месяца — месяц, 31 декабря — годовой Wrap.',
        body: `## Годовой Wrap
Сводка достижений: цели, задачи, серии, лучшие дни.`,
        related: ['reflection-daily']
      },
      {
        slug: 'success-diary',
        title: 'Дневник успеха',
        tldr: 'Архив всех рефлексий. Запись можно опубликовать через звёзды.',
        body: `## Публикация
Стоимость публикации — 1 звезда. Запись попадает в ленту «Актив».`,
        related: ['stars-earn', 'feed']
      },
      {
        slug: 'reflection-team',
        title: 'Командная рефлексия',
        tldr: 'Доступна на тарифе Команда. Заполняется лидом по итогам спринта.',
        body: `## Поля
Что получилось, что нет, договорённости.`,
        related: ['team-sprint']
      }
    ]
  },
  {
    id: 'social',
    title: 'Соцфункции',
    emoji: '👥',
    articles: [
      {
        slug: 'feed',
        title: 'Лента «Актив» и публикация достижений',
        tldr: 'Публикуйте посты-достижения, ставьте реакции, комментируйте.',
        body: `## Управление
Свои посты можно удалять и скрывать.`,
        related: ['quick-actions']
      },
      {
        slug: 'leaderboards',
        title: 'Лидерборды (персона/команда)',
        tldr: 'Топ-100 пользователей и топ-50 команд по очкам.',
        body: `## Метрика
XP с учётом активности за период (день/неделя/месяц).`,
        related: ['xp']
      },
      {
        slug: 'profiles-subs',
        title: 'Профили и подписки',
        tldr: 'Подписывайтесь на других — их активность появится в персональной ленте.',
        body: `## Каталог
Поиск по имени и профессиональному статусу.`,
        related: ['prof-statuses']
      },
      {
        slug: 'prof-statuses',
        title: 'Профессиональные статусы',
        tldr: 'Обязательны для попадания в Каталог пользователей.',
        body: `## Где задать
Профиль → Настройки → Профессиональный статус.`,
        related: ['profiles-subs']
      },
      {
        slug: 'contacts',
        title: 'Контакты и телефонная книга',
        tldr: 'Импорт контактов и привязка к сферам через \`contact_spheres\`.',
        body: `## Зачем
Видеть, кто из окружения связан с какой сферой и целью.`,
        related: []
      },
      {
        slug: 'group-chats',
        title: 'Групповые чаты',
        tldr: 'Самостоятельно вступать можно только в публичные чаты. В приватные приглашает админ.',
        body: `## Роли
Создатель, админ, модератор, участник.`,
        related: []
      }
    ]
  },
  {
    id: 'team',
    title: 'Команды (B2B)',
    emoji: '🏢',
    articles: [
      {
        slug: 'team-create',
        title: 'Создание команды и тарифы',
        tldr: 'Конфигуратор мест, оплата на год или помесячно. Цена за место уменьшается с объёмом.',
        body: `## Тарифы
- Стартовый: до 5 мест
- Бизнес: 6–25 мест
- Корпоративный: 26+ мест`,
        related: ['b2b-pricing']
      },
      {
        slug: 'team-sprint',
        title: 'Спринты 1–4 недели',
        tldr: 'SP конвертируются в XP, ежедневный snapshot через pg_cron.',
        body: `## Burndown
График доступен лиду команды.`,
        related: ['team-dashboard']
      },
      {
        slug: 'team-dashboard',
        title: 'Дашборд «Командная работа»',
        tldr: 'Вкладка на главном дашборде показывает прогресс команды.',
        body: `## Виджеты
- Активный спринт
- Burndown
- Топ участников`,
        related: []
      },
      {
        slug: 'team-public',
        title: 'Публичный профиль команды',
        tldr: 'Видит любой по ссылке. Внутренние данные защищены.',
        body: `## Что публично
Название, описание, общая статистика.`,
        related: []
      },
      {
        slug: 'team-simulator',
        title: 'Симулятор команды',
        tldr: 'Demo и Sandbox для пользователей без команды.',
        body: `## Demo
Готовый сценарий на 7 дней. Sandbox — пустая среда для экспериментов.`,
        related: []
      },
      {
        slug: 'team-roles',
        title: 'Роли и модерация',
        tldr: 'Owner, Admin, Member. Owner управляет тарифом и местами.',
        body: `## Передача владения
Через Профиль команды → Настройки → Передать владение.`,
        related: []
      }
    ]
  },
  {
    id: 'rewards',
    title: 'Звёзды, награды и геймификация',
    emoji: '⭐',
    articles: [
      {
        slug: 'stars-earn',
        title: 'Как зарабатывать звёзды',
        tldr: 'Минимум 15 минут фокуса на задаче. Лимит 7 звёзд в сутки.',
        body: `## Источники
- Завершение задач с фокус-таймером ≥15 мин
- Ежедневный логин
- Серии`,
        related: ['stars-login', 'streak-freeze']
      },
      {
        slug: 'stars-login',
        title: 'Ежедневный логин и серии',
        tldr: 'Каждый день логина — звезда. На 7-й день — бонус.',
        body: `## Серия
Прерывается, если пропустить день без заморозки.`,
        related: ['streak-freeze']
      },
      {
        slug: 'rewards-shop',
        title: 'Магазин наград и темы',
        tldr: 'Тратьте звёзды на темы, рамки аватара, бейджи.',
        body: `## Покупка
Атомарная RPC-операция, баланс не уйдёт в минус.`,
        related: ['badges']
      },
      {
        slug: 'streak-freeze',
        title: 'Заморозка серии (streak freeze)',
        tldr: 'Покупается за звёзды и автоматически расходуется при пропуске.',
        body: `## Стоимость
Уточняется в магазине — может меняться в акциях.`,
        related: ['stars-earn']
      },
      {
        slug: 'badges',
        title: 'Бейджи и рамки аватара',
        tldr: 'Активные бейджи показываются рядом с аватаром во всех экранах.',
        body: `## Лимит
Можно отображать до 2 бейджей одновременно.`,
        related: []
      }
    ]
  },
  {
    id: 'notifications',
    title: 'Уведомления',
    emoji: '🔔',
    articles: [
      {
        slug: 'push-notifs',
        title: 'Push-уведомления',
        tldr: 'Работают через Service Worker. Требуют разрешения браузера.',
        body: `## Что приходит
- Дедлайны задач
- Завершение Pomodoro
- Hooks (важные события)`,
        related: ['notif-throttle']
      },
      {
        slug: 'tg-notifs',
        title: 'Telegram-уведомления',
        tldr: 'Подключите бота в Профиле — уведомления будут дублироваться в Telegram.',
        body: `## Очередь
Сообщения уходят через таблицу \`telegram_queue\` и фоновую функцию.`,
        related: []
      },
      {
        slug: 'notif-throttle',
        title: 'Дедлайны и троттлинг',
        tldr: 'PRO upsell тост показывается не чаще 1 раза в 15 дней.',
        body: `## Принцип
Меньше шума — выше релевантность.`,
        related: []
      },
      {
        slug: 'notif-channels',
        title: 'Настройки каналов',
        tldr: 'Профиль → Настройки → Уведомления. Каждый канал переключается отдельно.',
        body: `## Каналы
Push, Email, Telegram.`,
        related: []
      }
    ]
  },
  {
    id: 'subscriptions',
    title: 'Подписки и оплата',
    emoji: '💳',
    articles: [
      {
        slug: 'pro-tariffs',
        title: 'Тарифы Focus / Profi / Premium / Команда',
        tldr: 'Focus бесплатный с лимитами. Profi/Premium расширяют лимиты, Команда — для групп.',
        body: `## Сравнение
- Focus: базовые модули
- Profi: больше задач, PDF-экспорт
- Premium: безлимит и аналитика
- Команда: спринты и ролевая модель`,
        related: ['session-limits', 'pay-yookassa']
      },
      {
        slug: 'session-limits',
        title: 'Лимиты одновременных сессий',
        tldr: 'Focus — 1 устройство, Profi — 2, Команда — 3.',
        body: `## Превышение
Самая старая сессия выходит автоматически.`,
        related: ['pro-tariffs']
      },
      {
        slug: 'pay-yookassa',
        title: 'Оплата через ЮKassa',
        tldr: 'Карта, СБП, ЮMoney. Подтверждение по GET-запросу webhook.',
        body: `## Безопасность
Сумма проверяется на сервере по таблице цен — клиент не может занизить цену.`,
        related: ['promo']
      },
      {
        slug: 'promo',
        title: 'Промокоды и активация',
        tldr: 'Промокод применяется на странице Upgrade. Активация атомарна.',
        body: `## Лимиты
Каждый код имеет максимум использований и срок жизни.`,
        related: []
      },
      {
        slug: 'subs-manage',
        title: 'Возврат, продление, смена тарифа',
        tldr: 'Продление автоматическое, смена тарифа доступна в любой момент.',
        body: `## Возврат
По запросу через support@top-focus.ru в течение 14 дней.`,
        related: []
      },
      {
        slug: 'geo-restriction',
        title: 'Geo-ограничения',
        tldr: 'Пользователи из РФ — только Email/Password (ФЗ-152).',
        body: `## Почему
Локальное хранение и обработка ПДн в РФ.`,
        related: ['signup']
      }
    ]
  },
  {
    id: 'partners',
    title: 'Партнёрские программы',
    emoji: '🤝',
    articles: [
      {
        slug: 'loyalty',
        title: 'Программа лояльности (физлица)',
        tldr: 'Возврат части стоимости подписки при приглашении друзей.',
        body: `## Условия
Подробности на странице «Партнёрская программа».`,
        related: ['referral-v2']
      },
      {
        slug: 'referral-v2',
        title: 'Реферальная программа v2 (B2B)',
        tldr: 'Для ИП и ООО. Требуется ИНН.',
        body: `## Что получает партнёр
Процент с подписок приведённых клиентов.`,
        related: ['active-referrals']
      },
      {
        slug: 'active-referrals',
        title: 'Активные рефералы и вехи',
        tldr: 'Активный = с оплаченной подпиской. Команда из N человек = N рефералов.',
        body: `## Падение
При истечении подписки реферал временно исключается.`,
        related: []
      },
      {
        slug: 'levels',
        title: 'Уровни (L1/L2) и ставки',
        tldr: 'L2 открывается с 51 активного реферала. Ставки по Premium растут до 30%.',
        body: `## Окупаемость
На Premium — 4 активных друга. Один годовой «Команда» 5+ окупает год использования.`,
        related: []
      },
      {
        slug: 'payouts',
        title: 'Выплаты, холд, счета',
        tldr: 'Порог выплаты 1 000 ₽, холд 14 дней. Для ЮЛ — счета и акты.',
        body: `## Запрос
Кабинет партнёра → «Запросить выплату».`,
        related: []
      },
      {
        slug: 'partner-faq',
        title: 'FAQ для партнёров',
        tldr: 'Ответы на типовые вопросы — встроены в раздел «Партнёрская программа».',
        body: `## Где искать
Партнёрская программа → вкладка FAQ.`,
        related: []
      }
    ]
  },
  {
    id: 'security',
    title: 'Безопасность и приватность',
    emoji: '🛡',
    articles: [
      {
        slug: 'fingerprint',
        title: 'Цифровой паспорт устройства',
        tldr: 'Canvas/WebGL fingerprint. Используется для антифрода.',
        body: `## Что хранится
Хеш отпечатка устройства, дата первой активации.`,
        related: ['security-sessions']
      },
      {
        slug: 'pin-lock-sec',
        title: 'PIN/FaceID и автоблокировка',
        tldr: 'Локальная защита. Срабатывает после 24 ч неактивности.',
        body: `## Биометрия
Доступна на устройствах с поддержкой WebAuthn.`,
        related: ['pin-lock']
      },
      {
        slug: 'security-sessions',
        title: 'Управление сессиями',
        tldr: 'Профиль → Безопасность → «Выйти со всех устройств».',
        body: `## Логика
Сбрасывает токены и кэш, перенаправляет на /auth.`,
        related: ['session-limits']
      },
      {
        slug: 'xss-ai-limits',
        title: 'Защита XSS и лимиты AI',
        tldr: 'Markdown санитизируется через DOMPurify. AI-запросы лимитированы по дням.',
        body: `## Лимиты AI
Ограничение токенов и rate-limit на пользователя.`,
        related: []
      },
      {
        slug: 'profile-visibility',
        title: 'Видимость профиля',
        tldr: 'Флаги show_* управляют тем, что видят другие пользователи.',
        body: `## Управление
Профиль → Настройки → Приватность.`,
        related: []
      },
      {
        slug: 'ban-list',
        title: 'Бан-лист и модерация',
        tldr: 'Админы могут банить по fingerprint и user_id.',
        body: `## Возражения
Через support@top-focus.ru с указанием email.`,
        related: []
      }
    ]
  },
  {
    id: 'data',
    title: 'Данные: экспорт, синхронизация, бэкап',
    emoji: '💾',
    articles: [
      {
        slug: 'export',
        title: 'Экспорт CSV / PDF',
        tldr: 'CSV — для таблиц, PDF — для печати. Кириллица поддержана.',
        body: `## Где
Профиль → Экспорт данных. Доступно для привычек, задач, финансов.`,
        related: []
      },
      {
        slug: 'backup',
        title: 'Локальный бэкап и восстановление',
        tldr: 'Создание JSON-снимка всех данных и восстановление в один клик.',
        body: `## Использование
Профиль → Резервная копия.`,
        related: ['sync']
      },
      {
        slug: 'sync',
        title: 'Синхронизация Supabase ↔ localStorage',
        tldr: 'Local-first: изменения сразу в localStorage, потом в облако.',
        body: `## Конфликты
Решаются по последнему updated_at. Real-time обновления приходят через канал \`habitflow-data-changed\`.`,
        related: ['troubleshoot-sync']
      },
      {
        slug: 'gcal',
        title: 'Google Calendar / ICS',
        tldr: 'Экспорт задач и привычек в .ics — импортируйте в любой календарь.',
        body: `## Файл
Скачивается локально и не отправляется на сервер.`,
        related: []
      }
    ]
  },
  {
    id: 'legal',
    title: 'Правовое и организация',
    emoji: '⚖️',
    articles: [
      {
        slug: 'legal-org',
        title: 'Реквизиты ООО',
        tldr: 'Юридическое лицо-оператор указано в правовых документах.',
        body: `## Где найти
Профиль → Правовое → Реквизиты.`,
        related: []
      },
      {
        slug: 'legal-docs',
        title: 'Согласия, политика, оферта',
        tldr: 'Все 7 документов доступны в любой момент в профиле.',
        body: `## Изменения
При значимых изменениях вы увидите модальное окно повторного согласия.`,
        related: ['legal-wall']
      },
      {
        slug: 'fz152',
        title: 'ФЗ-152 и хранение в РФ',
        tldr: 'Для пользователей из РФ данные хранятся на серверах в РФ.',
        body: `## Подтверждение
Указано в Политике конфиденциальности.`,
        related: ['geo-restriction']
      },
      {
        slug: 'support',
        title: 'Контакты поддержки',
        tldr: 'support@top-focus.ru — ответ в течение 24 часов.',
        body: `## Что приложить
Скриншот, версию приложения, шаги воспроизведения.`,
        related: []
      }
    ]
  },
  {
    id: 'troubleshoot',
    title: 'FAQ и решение проблем',
    emoji: '🛠',
    articles: [
      {
        slug: 'troubleshoot-notifs',
        title: 'Не приходят уведомления',
        tldr: 'Проверьте разрешения браузера и настройки каналов.',
        body: `## Чек-лист
- Разрешение на push в браузере
- Включён нужный канал в Настройках
- На iOS — приложение установлено через «На экран Домой»`,
        related: ['push-notifs']
      },
      {
        slug: 'troubleshoot-sync',
        title: 'Не сохраняются данные / проблемы синхронизации',
        tldr: 'Проверьте интернет, обновите страницу, при необходимости — выйдите и войдите.',
        body: `## Локальные данные
Не теряются: при подключении сети уйдут в облако.`,
        related: ['sync']
      },
      {
        slug: 'troubleshoot-payment',
        title: 'Оплата прошла, но PRO не активировался',
        tldr: 'Подождите 1–2 минуты, обновите страницу. Если не помогло — напишите в поддержку.',
        body: `## Что приложить
Чек ЮKassa и email аккаунта.`,
        related: ['support']
      },
      {
        slug: 'troubleshoot-reset',
        title: 'Сбросить онбординг / PIN / прогресс',
        tldr: 'Онбординг — снова через иконку плёнки. PIN — через выход. Прогресс — обращение в поддержку.',
        body: `## Удаление аккаунта
Профиль → Настройки → «Удалить аккаунт».`,
        related: ['support']
      },
      {
        slug: 'troubleshoot-pwa',
        title: 'Установка PWA в Telegram WebView',
        tldr: 'Откройте https://top-focus.ru в системном браузере, не в Telegram-окне.',
        body: `## Почему
WebView Telegram кэширует страницу — установка PWA может не работать.`,
        related: ['install-pwa']
      },
      {
        slug: 'troubleshoot-delete',
        title: 'Запросить удаление аккаунта',
        tldr: 'Профиль → Настройки → «Удалить аккаунт». Удаление необратимо.',
        body: `## Что удалится
Все ваши данные, посты, рефлексии, статистика.`,
        related: ['support']
      }
    ]
  },
  {
    id: 'glossary',
    title: 'Глоссарий и горячие клавиши',
    emoji: '📖',
    articles: [
      {
        slug: 'glossary',
        title: 'Глоссарий терминов',
        tldr: 'Краткие определения ключевых сокращений и терминов.',
        body: `## Термины
- **FP** — Focus Points, очки фокуса
- **XP** — Experience Points, опыт пользователя
- **SP** — Sprint Points, очки спринта (команда)
- **Main Task** — Главная задача дня
- **Balance Spread** — разница между лучшей и худшей сферой
- **Focus Window** — рекомендуемое окно глубокой работы`,
        related: []
      },
      {
        slug: 'shortcuts',
        title: 'Горячие клавиши и жесты',
        tldr: 'Клавиатурные сочетания и свайпы для ускорения работы.',
        body: `## Жесты
- Свайп влево/вправо в аккордеоне дашборда
- Долгое нажатие на карточку — контекстное меню

## Клавиатура (десктоп)
- \`/\` — фокус на поиск
- \`Esc\` — закрыть диалог`,
        related: []
      },
      {
        slug: 'changelog',
        title: 'История версий',
        tldr: 'Список заметных изменений с датами.',
        body: `## Где смотреть
Профиль → О приложении → Изменения.`,
        related: []
      }
    ]
  }
];

// English mirror — concise summaries (structural parity).
// For brevity, EN reuses RU-derived structure. Localize fully later.
export const HELP_CONTENT_EN: HelpSection[] = HELP_CONTENT_RU.map((s) => ({
  ...s,
  articles: s.articles.map((a) => ({
    ...a,
    // English UI fallback: keep title/body in RU as content has not been
    // fully localized yet. Section labels are translated in the page shell.
  })),
}));

export function getHelpContent(lang: HelpLang): HelpSection[] {
  return lang === 'en' ? HELP_CONTENT_EN : HELP_CONTENT_RU;
}

export function findArticle(
  content: HelpSection[],
  slug: string
): { section: HelpSection; article: HelpArticle } | null {
  for (const section of content) {
    const article = section.articles.find((a) => a.slug === slug);
    if (article) return { section, article };
  }
  return null;
}

export function getAllArticles(content: HelpSection[]): Array<HelpArticle & { sectionId: string; sectionTitle: string }> {
  return content.flatMap((s) =>
    s.articles.map((a) => ({ ...a, sectionId: s.id, sectionTitle: s.title }))
  );
}